import { useState, useCallback } from 'react';
import type { Week, Song, Hymn, Pianist, Conductor } from '../types';
import { SongRow } from './SongRow';
import { formatDateDisplay } from '../utils/date';

interface WeekCardProps {
  week: Week;
  weekIndex: number;
  pianists: Pianist[];
  conductors: Conductor[];
  hymns: Hymn[];
  getHymnDisplayText: (hymnId: string) => string;
  getPianistName: (pianistId: string) => string;
  getConductorName: (conductorId: string) => string;
  onUpdateWeek: (field: keyof Week, value: string) => void;
  onUpdateSong: (songIndex: number, field: keyof Song, value: string | boolean) => void;
  onAddSong: () => void;
  onRemoveSong: (songIndex: number) => void;
  onDeleteWeek?: () => void;
  onAuthRequired?: () => void;
  isAdminMode?: boolean;
  disabled?: boolean;
  initiallyExpanded?: boolean;
}

export function WeekCard({
  week,
  weekIndex,
  pianists,
  conductors,
  hymns,
  getHymnDisplayText,
  getPianistName,
  getConductorName,
  onUpdateWeek,
  onUpdateSong,
  onAddSong,
  onRemoveSong,
  onDeleteWeek,
  onAuthRequired,
  isAdminMode = false,
  disabled = false,
  initiallyExpanded = false,
}: WeekCardProps) {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const pianistName = getPianistName(week.pianist);
  const conductorName = getConductorName(week.conductor);
  const dateDisplay = formatDateDisplay(week.date);

  return (
    <div
      className={`week-card ${isExpanded ? 'week-card--expanded' : ''}`}
      id={`week-${weekIndex}`}
    >
      <div className="week-card__header" onClick={toggleExpanded}>
        <div className="week-card__header-info">
          <span className="week-card__expand-icon">&#9658;</span>
          <span className="week-card__date">{dateDisplay}</span>
          <span className="week-card__pianist">
            {pianistName}
            {conductorName ? ` | ${conductorName}` : ''}
          </span>
        </div>
      </div>

      <div className="week-card__content" style={{ position: 'relative' }}>
        {disabled && onAuthRequired && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              cursor: 'pointer',
            }}
            onClick={onAuthRequired}
            title="Click to log in and edit"
          />
        )}
        <div className="week-card__meta">
          <div className="week-card__meta-field">
            <label>Date</label>
            <input
              type="date"
              value={week.date}
              onChange={(e) => onUpdateWeek('date', e.target.value)}
              disabled={!isAdminMode || disabled}
            />
          </div>
          <div className="week-card__meta-field">
            <label>Pianist/Organist</label>
            <select
              value={week.pianist}
              onChange={(e) => onUpdateWeek('pianist', e.target.value)}
              disabled={disabled}
            >
              <option value="">Select Pianist/Organist</option>
              {pianists.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="week-card__meta-field">
            <label>Conductor</label>
            <select
              value={week.conductor}
              onChange={(e) => onUpdateWeek('conductor', e.target.value)}
              disabled={disabled}
            >
              <option value="">Select Conductor</option>
              {conductors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {week.songs.map((song, songIndex) => (
          <SongRow
            key={song.id}
            song={song}
            songIndex={songIndex}
            hymns={hymns}
            getHymnDisplayText={getHymnDisplayText}
            onUpdate={(field, value) => onUpdateSong(songIndex, field, value)}
            onRemove={() => onRemoveSong(songIndex)}
            canRemove={week.songs.length > 3}
            disabled={disabled}
          />
        ))}

        <div className="week-card__meta" style={{ marginTop: '15px' }}>
          <div
            className="week-card__meta-field"
            style={{ gridColumn: 'span 2' }}
          >
            <label>Notes</label>
            <textarea
              placeholder="Additional notes for this week..."
              value={week.notes || ''}
              onChange={(e) => onUpdateWeek('notes', e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="week-card__actions">
          <button
            className="btn-secondary"
            onClick={onAddSong}
            disabled={disabled || week.songs.length >= 5}
          >
            + Add Song
          </button>
          {isAdminMode && onDeleteWeek && (
            <button
              className="btn-danger"
              onClick={onDeleteWeek}
              disabled={disabled}
            >
              Delete Week
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
